import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import { generalLedgerService, GLJournalEntryDTO } from '../../../../services/accountingService';
import { useCurrencyFormat } from '../../../../hooks/useCurrency';
import { ArrowLeft, Ban } from 'lucide-react';
import { getServerErrorMessage } from '../../../../utils/errorUtils';

const JournalEntryDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const fmt = useCurrencyFormat();

  const [entry, setEntry] = useState<GLJournalEntryDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [voiding, setVoiding] = useState(false);
  const [showVoidForm, setShowVoidForm] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const langCode = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';
  const fmtDate = (d: string) => new Date(d).toLocaleDateString(langCode);

  const load = () => {
    if (!id) return;
    setLoading(true);
    generalLedgerService.getJournalEntryById(Number(id))
      .then((res) => setEntry((res?.data as any)?.data ?? res?.data ?? null))
      .finally(() => setLoading(false));
  };

  useEffect(load, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVoid = async () => {
    if (!id || !voidReason.trim()) return;
    setVoiding(true);
    setError(null);
    try {
      await generalLedgerService.voidJournalEntry(Number(id), voidReason.trim());
      setShowVoidForm(false);
      load();
    } catch (err: any) {
      setError(getServerErrorMessage(err) ?? t('accounting.gl.failedToVoid'));
    } finally {
      setVoiding(false);
    }
  };

  const canVoid = entry?.status === 'Posted' && entry.sourceModule === 'Manual';

  return (
    <Layout>
      <div style={{ padding: '0 20px 40px', maxWidth: 900, margin: '0 auto' }}>
        <div className="ims-top-bar" style={{ marginBottom: 20 }}>
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/accounting/journal-entries')}>
              <ArrowLeft size={13} /> {t('accounting.gl.journalEntries')}
            </button>
            <div>
              <h1 className="ims-page-title">{entry?.entryNumber || t('common.loading')}</h1>
              {entry && <p className="ims-page-subtitle">{fmtDate(entry.entryDate)} · {entry.description}</p>}
            </div>
          </div>
          {canVoid && (
            <div className="ims-top-bar-right">
              <button className="ims-btn ims-btn-danger" onClick={() => setShowVoidForm((v) => !v)}>
                <Ban size={15} strokeWidth={1.75} /> {t('accounting.gl.voidEntry')}
              </button>
            </div>
          )}
        </div>

        {error && <div className="ims-alert ims-alert-error" style={{ marginBottom: 16 }}>{error}</div>}

        {showVoidForm && (
          <div className="ims-card" style={{ marginBottom: 16 }}>
            <div className="ims-card-body">
              <div className="ims-form-group">
                <label className="ims-form-label">{t('accounting.gl.voidReason')}</label>
                <textarea className="ims-form-control ims-textarea" rows={2} value={voidReason}
                  onChange={(e) => setVoidReason(e.target.value)} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="ims-btn ims-btn-danger" onClick={handleVoid} disabled={voiding || !voidReason.trim()}>
                  {voiding ? t('common.saving') : t('accounting.gl.confirmVoid')}
                </button>
                <button className="ims-btn ims-btn-secondary" onClick={() => setShowVoidForm(false)}>{t('common.cancel')}</button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div className="spinner-border" role="status"><span className="visually-hidden">{t('common.loading')}</span></div>
          </div>
        ) : entry && (
          <div className="ims-card">
            <div className="ims-table-scroll">
              <table className="ims-table">
                <thead>
                  <tr>
                    <th className="ims-th">{t('accounting.coa.code')}</th>
                    <th className="ims-th">{t('accounting.coa.name')}</th>
                    <th className="ims-th">{t('common.description')}</th>
                    <th className="ims-th ims-th--num" style={{ width: 130 }}>{t('accounting.ledger.debit')}</th>
                    <th className="ims-th ims-th--num" style={{ width: 130 }}>{t('accounting.ledger.credit')}</th>
                  </tr>
                </thead>
                <tbody>
                  {entry.lines.map((l) => (
                    <tr key={l.id} className="ims-tr">
                      <td className="ims-td"><code>{l.accountCode}</code></td>
                      <td className="ims-td">{l.accountName}</td>
                      <td className="ims-td">{l.description || '—'}</td>
                      <td className="ims-td ims-td--line-total">{l.debit > 0 ? fmt(l.debit) : ''}</td>
                      <td className="ims-td ims-td--line-total">{l.credit > 0 ? fmt(l.credit) : ''}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="ims-td" colSpan={3}><strong>{t('accounting.gl.total')}</strong></td>
                    <td className="ims-td ims-td--line-total"><strong>{fmt(entry.totalDebit)}</strong></td>
                    <td className="ims-td ims-td--line-total"><strong>{fmt(entry.totalCredit)}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default JournalEntryDetailPage;
